import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FaMapMarkerAlt, FaBriefcase, FaArrowLeft } from 'react-icons/fa';

import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace(
  '/backend/api',
  '/uploads',
);

import WithBackground from '../Common/WithBackground';
import Header from '../Header';
import WorkerKontakt from '../WorkerKontakt';

import './style.css';

const JobOfferDetail = ({ bg, overlay }) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/offers.php?id=${id}&lang=${i18n.language}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !data.offer) {
          throw new Error('Offer not found');
        }

        const processedOffer = {
          ...data.offer,
          ...(data.offer.translations[i18n.language] ||
            data.offer.translations.pl),
        };

        setOffer(processedOffer);
      } catch (error) {
        console.error('Error fetching offer:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOffer();
    }
  }, [id, i18n.language]);

  if (loading) {
    return (
      <div className='JobOfferDetail__loading'>
        <Header />
        <div className='JobOfferDetail__loading-content'>
          <div className='JobOfferDetail__loading-inner'>
            <h1>Loading Job Offer...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className='JobOfferDetail__not-found'>
        <Header />
        <div className='JobOfferDetail__not-found-content'>
          <div className='JobOfferDetail__not-found-inner'>
            <h1>Job Offer Not Found</h1>
            <p>
              The job offer you&apos;re looking for doesn&apos;t exist or has
              been removed.
            </p>
            <Link to='/oferty-pracy' className='btn'>
              {t('navigation.vacancies')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const detailContent = (
    <div className='JobOfferDetail'>
      <Header />

      <main className='JobOfferDetail__main'>
        <div className='JobOfferDetail__container'>
          {/* Breadcrumb Navigation */}
          <nav className='JobOfferDetail__breadcrumb'>
            <Link to='/' className='JobOfferDetail__breadcrumb-link'>
              {t('navigation.home')}
            </Link>
            <span className='JobOfferDetail__breadcrumb-separator'>/</span>
            <Link
              to='/oferty-pracy'
              className='JobOfferDetail__breadcrumb-link'
            >
              {t('navigation.vacancies')}
            </Link>
            <span className='JobOfferDetail__breadcrumb-separator'>/</span>
            <span className='JobOfferDetail__breadcrumb-current'>
              {offer.title}
            </span>
          </nav>

          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className='JobOfferDetail__back-button'
          >
            <FaArrowLeft className='JobOfferDetail__back-icon' />
            {t('common.back') || 'Back'}
          </button>

          {/* Job Offer Header */}
          <div className='JobOfferDetail__header'>
            <div className='JobOfferDetail__header-content'>
              <div className='JobOfferDetail__header-text'>
                <h1 className='JobOfferDetail__title'>{offer.title}</h1>
                <div className='JobOfferDetail__meta'>
                  <div className='JobOfferDetail__meta-item'>
                    <FaMapMarkerAlt className='JobOfferDetail__meta-icon' />
                    <span>{offer.location}</span>
                  </div>
                  <div className='JobOfferDetail__meta-item'>
                    <FaBriefcase className='JobOfferDetail__meta-icon' />
                    <span>{offer.description}</span>
                  </div>
                </div>
              </div>

              <div className='JobOfferDetail__header-image'>
                <img
                  src={`${UPLOADS_BASE_URL}/${offer.image}`}
                  alt={offer.title}
                  onError={(e) => {
                    console.log('Detail image failed to load:', offer.image);
                    e.target.style.display = 'none';
                    const placeholder = e.target.nextSibling;
                    if (placeholder) {
                      placeholder.style.display = 'flex';
                    }
                  }}
                  onLoad={() => {
                    console.log(
                      'Detail image loaded successfully:',
                      offer.image,
                    );
                  }}
                />
                <div className='JobOfferDetail__image-placeholder'>
                  <FaBriefcase />
                </div>
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div className='JobOfferDetail__content'>
            <div className='JobOfferDetail__description'>
              <h2 className='JobOfferDetail__section-title'>
                {t('jobDetail.description') || 'Opis stanowiska'}
              </h2>
              <div className='JobOfferDetail__description-content'>
                {offer.alldescription &&
                  offer.alldescription
                    .split('\n')
                    .map((line, index) => {
                      const trimmedLine = line.trim();
                      if (!trimmedLine) return null;

                      // Check if line starts with emoji or bullet point
                      const emojiRegex =
                        /^[\p{Emoji}\p{Emoji_Component}▪▫✔]/u;
                      const isHeading = emojiRegex.test(trimmedLine);

                      return (
                        <p
                          key={index}
                          className={`JobOfferDetail__description-text ${isHeading ? 'JobOfferDetail__description-heading' : ''}`}
                        >
                          {trimmedLine}
                        </p>
                      );
                    })
                    .filter(Boolean)}
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Contact Section */}
      <WorkerKontakt />
    </div>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {detailContent}
    </WithBackground>
  ) : (
    detailContent
  );
};

JobOfferDetail.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

JobOfferDetail.defaultProps = {
  bg: null,
  overlay: 'dark',
};

export default JobOfferDetail;
